import React, { useCallback, useEffect, useMemo, useRef, useState, Fragment } from 'react';
import { observer, useObserver } from 'mobx-react-lite';
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import { FormControlLabel, makeStyles, Radio, RadioGroup } from '@material-ui/core';
import useRoom from './hooks/useRoom';
import useBooking from './hooks/useBooking';
import SearchResults from './components/SearchResults';
import Search from './components/Search';
import Bookings from './components/Bookings';
import EventLabel from '../../components/EventLabel/EventLabel';
import CustomInput from '../../components/custominput/CustomInput';
import { Transition, Dialog } from '@headlessui/react';

import './hotel.css';
import DateListbox from './components/DateListbox';
import Snackbar from './components/Snackbar';
import OrderAutoComplete from './components/OrderAutoComplete';

const useStyles = makeStyles(theme => ({
  root: {
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.178)',
    },
    marginLeft: 0,
    marginRight: 0,
  },
  label: {
    '&.Mui-disabled': {
      color: 'inherit !important',
    },
    color: 'inherit',
    fontWeight: 'inherit',
    fontSize: 'inherit',
    fontFamily: 'inherit',
  },
}));

function Hotel(props) {
  const [selectedAllotmentSize, setSelectedRoomSize] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [hasFetched, setHasFetched] = useState(false);

  const classes = useStyles();
  const { settings } = props;
  const { eventId, orders, displayEventSettings, environment, updateOrderRow, updateOrder, hotelStore, ordersLoaded, breakouts } = props;
  const { currency } = environment;

  const {
    hotelAllotments,
    hotels,
    fetchHotels,
    setSelection,
    fetchBookableRoomSizes,
    fetchAvailableAllotmentsBySelection,
    setCheckinDate,
    setRoomSize,
    setRequestedRoomSize,
    setCheckoutDate,
    configureDefaultDates,
    requestedRoomSize,
    hideNoResultMessage,
    showNoResultMessage,
    bookableRoomSizes,
    gapDates,
    checkinDate,
    roomCount,
    setRoomCount,
    checkoutDate,
    fetching,
    fullbooked,
    bookableDates,
    stay,
    book,
    setStay,
    validateReferencePerson,
    setBook,
    search,
    searchResults,
    setInitialized,
    removeOrderExtra,
    setOrderExtra,
    initialized,
    roomSizes: selectedRoomSizes,
    fetchAllotmentStatus,
    fetched,
    roomMates,
    validateBooking,
    setSettings,
    fetchRoomMates,
  } = hotelStore;

  const {
    useSelection,
    selection,
    lockStartDate,
    lockEndDate,
    hideStartDate,
    hideEndDate,
    hideRoomCountSelector,
    required,
    disableChangeLinkBooking,
    disableRoomateBooking,
    selectRoomMateFromOrder = false,
    maxBookCount = undefined,
    hideBookingDetails = false,
    hideRegisterLater = false,
    oneSizePerSearch = false
  } = settings;

  const order = orders.find(({ leader }) => leader);
  const orderRows = order?.orderRow?.filter(row => row.hotelAllotmentPricingId) ?? [];
  const hotelOrderRows = orderRows?.filter(row => row.hotelGroupId && !row.delete) ?? [];

  const isChangeLink = orders?.[0]?.badgeNo;
  const isGroupMember = orders?.[0]?.orderGroupId && !orders?.[0]?.leader;

  const handleSnackbar = useCallback(
    message => {
      setSnackbarMessage(message);
      setShowSnackbar(true);
    },
    [setSnackbarMessage, setShowSnackbar]
  );

  // Move this logic to the hotelStore later on
  const { onChange: handleRoomChange, roomSizes } = useRoom({ updateOrderRow, order, hotelAllotments, hotelOrderRows, lang: environment.lang });

  // Move this hook logic in the hotelStore later on
  const {
    onChange: handleBookingChange,
    onAdd: handelBookingAdd,
    onDelete: handelBookingDelete,
    bookings,
    handleFullBooked,
    fullbooked: fullbokedAllotments = [],
  } = useBooking({
    hotels,
    order,
    breakouts,
    onSnackbar: handleSnackbar,
    hotelOrderRows,
    roomSizes,
    validateBooking,
    startDate: checkinDate,
    endDate: checkoutDate,
    hotelAllotments,
    updateOrderRow,
    eventId,
    lang: environment.lang,
  });

  const clearBookings = useCallback(() => {
    if (order?.orderRow?.some(({ hotelAllotmentPricingId, orderRowId }) => hotelAllotmentPricingId && orderRowId < 1)) {
      const orderRows = order?.orderRow?.filter(({ hotelAllotmentPricingId, orderRowId }) => hotelAllotmentPricingId && orderRowId < 1);
      orderRows?.forEach(orderRow => {
        handelBookingDelete(orderRow.hotelGroupId);
      });
    }
  }, [order, handelBookingDelete]);

  const handleStay = async condition => {
    if (condition === false) {
      if (!orders?.[0]?.badgeNo) {
        clearBookings();
        removeOrderExtra('HotelReferencePerson');
      }
      setSelectedRoomSize(null);
      setBook(false);
    }

    if (disableRoomateBooking && condition === true) {
      setBook(true);
    }

    if (condition === true && useSelection && selection?.length === 1 && checkinDate && checkoutDate) {
      await search();
    }

    setStay(condition);
  };

  const handleDateChange = useCallback(
    async (date, type) => {
      setCheckinDate(type === 'start' ? date : checkinDate);
      setCheckoutDate(type === 'end' ? date : checkoutDate);

      // Clear previous order rows when switching dates
      clearBookings();
      setSelectedRoomSize(null);

      // make availaility check based on the date range
      await search();
    },
    [order, eventId, search, clearBookings, setCheckinDate, setCheckoutDate, checkinDate, checkoutDate]
  );

  // Can only be used with selection and selection length is one
  const handleAddInternalRoomate = async (name, orderId) => {
    if (selection?.length !== 1 || !selectRoomMateFromOrder) return;

    removeOrderExtra('HotelReferencePerson');

    const hotelGroupId = hotelOrderRows?.find(({ hotelGroupId }) => hotelGroupId)?.hotelGroupId;
    const rooms = hotelOrderRows?.find(({ hotelGroupId }) => hotelGroupId)?.rooms;
    const orderRooms = rooms?.filter(r => r.orderId === order.orderId);

    const newRoomId = () => {
      const roomsIds = orderRooms.filter(room => room.roomId < 0).map(room => room.roomId);
      if (!roomsIds.length) return -1;
      const newRoomId = (Math.min(...roomsIds) ?? 0) - 1;
      return newRoomId;
    };

    const newOrderRows = order?.orderRow?.map(or => {
      if (or.hotelGroupId === hotelGroupId) {
        const newRooms = or.rooms?.filter(r => r.orderId === order.orderId);
        return {
          ...or,
          rooms: [
            ...newRooms,
            {
              roomId: newRoomId(),
              roomMate: name,
              orderId: orderId,
              eventId: eventId,
              orderRowId: or.orderRowId,
              external: false,
            },
          ],
        };
      }
      return or;
    });

    setOrderExtra('HotelReferencePerson', name);
    updateOrder({ ...order, orderRow: newOrderRows });
    setStay(true);
  };

  const handleRoomSizeChange = async roomSize => {
    if (selection?.length === 1 && useSelection && searchResults?.length) {
      const requestedAllotmentIds = searchResults?.find(({ asRoomSize }) => asRoomSize === roomSize)?.allotmentPricingIds;
      const allotments = hotelAllotments.filter(({ hotelRoomType, hotelAllotmentPricing }) => {
        return hotelRoomType.hotelRoomTypeId === selection[0] && hotelAllotmentPricing.some(hap => requestedAllotmentIds?.includes(hap.allotmentPricingId));
      });
      const pricings = allotments.map(({ hotelAllotmentPricing }) => ({
        allotmentPricingId: hotelAllotmentPricing?.find(({ roomSize: _roomSize }) => _roomSize === roomSize)?.allotmentPricingId,
        vatPercent: hotelAllotmentPricing?.find(({ roomSize: _roomSize }) => _roomSize === roomSize)?.vatPercent,
        price: hotelAllotmentPricing?.find(({ roomSize: _roomSize }) => _roomSize === roomSize)?.price,
      }));

      if (!pricings?.length) {
        return;
      }

      setSelectedRoomSize(roomSize);

      // Check the status for the selected room size
      await fetchAllotmentStatus(requestedAllotmentIds);

      // Clear previous order rows when switching from living with room mate to living alone
      clearBookings();

      if (roomSize === 1) {
        setBook(true);
        setStay(true);
      } else {
        setBook(false);
        validateReferencePerson();
      }

      setRequestedRoomSize(roomSize);

      const nonCreatedOrderRows = order.orderRow?.filter(row => row.orderRowId < 0)?.map(row => row.orderRowId) ?? [];

      let newOrderRowId = -1;

      if (nonCreatedOrderRows.length > 0) {
        newOrderRowId = (Math.min(...nonCreatedOrderRows) ?? 0) - 1;
      }

      pricings?.forEach(pricing => {
        updateOrderRow(
          {
            orderRowId: newOrderRowId,
            amount: pricing.price ?? 0,
            comment: '',
            discount: 0,
            eventId: null,
            orderId: order?.orderId ?? -1,
            quantity: 1,
            status: null,
            vatPercent: pricing.vatPercent || 0,
            deleted: false,
            groupId: null,
            hotelAllotmentPricingId: pricing.allotmentPricingId,
            hotelGroupId: 1,
            rooms: [
              {
                roomId: -1,
                orderId: order.orderId,
                orderRowId: newOrderRowId,
                external: false,
                roomMate: order?.person?.firstName && order?.person?.lastName ? `${order?.person?.firstName} ${order?.person?.lastName}` : '',
                eventId,
              },
            ],
          },
          false,
          order?.orderId
        );
        --newOrderRowId;
      });

      // switch from living alone to living with room mate remove the order extra ref
      if (roomSize === 1) {
        removeOrderExtra('HotelReferencePerson');
      }

      validateBooking();
    }
  };

  const allowedBooking = useMemo(() => {
    const order = orders.find(({ leader }) => leader);
    if (order?.badgeNo && disableChangeLinkBooking) return false;
    return true;
  }, [orders, disableChangeLinkBooking]);

  //fetch the available room sizes based on the selection.
  useEffect(() => {
    async function fetch() {
      await fetchBookableRoomSizes(environment.eventId);
      await fetchHotels(environment.eventId);
      setHasFetched(true);
    }

    if (!hasFetched && environment.eventId) {
      fetch();
    }
  }, []);

  useEffect(() => {
    async function init() {
      if (ordersLoaded && hasFetched) {
        setSettings(settings);
        // Configure start and end date based on the settings (can be configured in the editoro)
        configureDefaultDates(settings);

        if (disableChangeLinkBooking || (required && hotelOrderRows?.length === 0 && disableRoomateBooking)) {
          setBook(true);
        }

        if (useSelection && selection?.length && allowedBooking) {
          setSelection(selection ?? []);

          if (selection.length === 1) {
            await fetchAvailableAllotmentsBySelection(environment.eventId);
          }
        }

        if (required && allowedBooking) {
          if (useSelection && selection?.length === 1) {
            setStay(true);
            await search();
          } else {
            setStay(true);
          }
        }

        if (hotelOrderRows?.length > 0 && selection?.length === 1 && order?.badgeNo && selectRoomMateFromOrder) {
          setStay(true);
          setBook(true);
        }

        if (settings?.selectRoomMateFromOrder) {
          await fetchRoomMates(environment.eventId, order?.orderId);
        }

        let isValid = required && allowedBooking && hotelOrderRows?.length === 0 ? false : true;

        if (isGroupMember && isChangeLink) {
          isValid = true;
          setBook(false);
          setStay(false);
        }

        setInitialized(isValid);
      }
    }
    init();
  }, [ordersLoaded, hasFetched]);

  useEffect(() => {}, [isGroupMember, isChangeLink]);

  const referencePerson = order?.orderExtra?.find(oe => oe?.name === 'HotelReferencePerson')?.value;

  if (isGroupMember && isChangeLink) {
    return (
      <div className="p-2 px-3 rounded-md border border-sky-300 font-me bg-sky-50 text-sky-900 flex gap-x-2 items-center">
        <i className="ri-information-line text-sky-500 mt-1" /> <EventLabel name="OnlyGroupLeaderCanBookRooms" groupName="CMS.Hotel" />
      </div>
    );
  }

  if (fullbooked) {
    return <EventLabel name="NoHotelAvailable" groupName="CMS.Hotel" />;
  }

  if (hotels?.length === 0 && fetched) {
    return (
      <div className="bg-amber-100 text-amber-900 rounded-md px-2 py-1 gap-x-1 text-center flex items-center justify-center">
        <i className="ri-information-line text-amber-500 font-bold" /> <EventLabel name="NoHotelAddedOnEvent" groupName="CMS.Hotel" />
      </div>
    );
  }

  // If using selection and selection is set to one hotel room type
  if (useSelection && selection?.length === 1 && allowedBooking) {
    if (hotelOrderRows.some(({ orderRowId }) => orderRowId > 0) && selectRoomMateFromOrder) return null;

    return (
      <div className="cms-hotel-wrap w-full">
        {!required && (
          <div className="content-wrapper cms-question questionnaire-group mb-4" style={{ backgroundColor: 'inherit' }}>
            <label>
              <EventLabel name="WantToStayAtHotel" groupName="CMS.Hotel" /> <span className="text-danger ml-1">*</span>
            </label>
            <RadioGroup
              className="cms-question checkbox-group"
              aria-labelledby="stay-at-hotel-radio-group"
              onChange={(e, value) => {
                handleStay(value === 'true' ? true : false);
              }}
            >
              <FormControlLabel
                className="option font-normal"
                classes={classes}
                control={<Radio checked={stay === true} checkedIcon={<RadioButtonCheckedIcon className="cms-checkbox" />} />}
                value={true}
                label={<EventLabel name="Yes" groupName="CMS.Hotel" />}
              />
              <FormControlLabel
                className="option font-normal"
                classes={classes}
                control={<Radio checked={stay === false} checkedIcon={<RadioButtonCheckedIcon className="cms-checkbox" />} />}
                value={false}
                label={<EventLabel name="No" groupName="CMS.Hotel" />}
              />
            </RadioGroup>
          </div>
        )}

        {(!hideStartDate || !hideEndDate) && stay && (
          <div className="content-wrapper cms-question questionnaire-group mb-4 w-full" style={{ backgroundColor: 'inherit' }}>
            <label>
              <EventLabel name="WhenDoYouWannaStay" groupName="CMS.Hotel" />
            </label>
            <div className="w-full flex mt-4 flex-wrap">
              {!hideStartDate && (
                <div className="w-full md:w-fit">
                  <label className="mb-4">
                    <EventLabel name="CheckinDate" groupName="CMS.Hotel" />
                  </label>
                  <DateListbox disabled={lockStartDate} date={checkinDate} onChange={date => handleDateChange(date, 'start')} dates={bookableDates} />
                </div>
              )}
              {!hideStartDate && !hideEndDate && (
                <div className="hidden items-end md:flex  mb-2 mx-2 text-sm text-neutral-600 font-normal">
                  <EventLabel name="ToDateSpan" groupName="CMS.Hotel" />
                </div>
              )}
              {!hideEndDate && (
                <div className=" mt-4 md:mt-0 w-full md:w-fit">
                  <label className="mb-4">
                    <EventLabel name="CheckOutDate" groupName="CMS.Hotel" />
                  </label>
                  <DateListbox
                    disabled={lockEndDate}
                    date={checkoutDate}
                    onChange={date => handleDateChange(date, 'end')}
                    dates={[
                      ...bookableDates?.filter(date => +new Date(date) > +new Date(checkinDate)),
                      ...[gapDates?.[+new Date(checkinDate)] ? gapDates?.[+new Date(checkinDate)] : null],
                    ].sort((a, b) => +new Date(a) - +new Date(b))}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {searchResults?.length === 0 && fetched && (
          <p>
            <EventLabel name="NoResultBasedOnDateRange" groupName="CMS.Hotel" />
          </p>
        )}
        {searchResults?.length > 0 && stay && (maxBookCount ? maxBookCount > hotelOrderRows?.length : true) && (
          <div className="content-wrapper cms-question questionnaire-group" style={{ backgroundColor: 'inherit' }}>
            <label>
              <EventLabel name="WantToBookARoom" groupName="CMS.Hotel" />
            </label>
            <RadioGroup
              className="cms-question checkbox-group"
              aria-labelledby="book-hotel-radio-group"
              onChange={(e, value) => handleRoomSizeChange(parseInt(value))}
            >
              {bookableRoomSizes?.map(size => (
                <FormControlLabel
                  className="option font-normal"
                  classes={classes}
                  control={
                    <Radio
                      checked={selectedAllotmentSize === size || requestedRoomSize === size}
                      checkedIcon={<RadioButtonCheckedIcon className="cms-checkbox" />}
                    />
                  }
                  value={size}
                  label={<EventLabel name={`IWantToLiveInRoomSize${size}`} groupName="CMS.Hotel" />}
                />
              ))}
            </RadioGroup>
          </div>
        )}
        {(selectedAllotmentSize > 1 || requestedRoomSize > 1) && (
          <div className="content-wrapper cms-field mt-4" style={{ backgroundColor: 'inherit' }}>
            {selectRoomMateFromOrder ? (
              <OrderAutoComplete
                orderId={order?.orderId}
                eventId={eventId}
                referencePerson={hotelOrderRows?.[0]?.rooms?.find(room => room?.orderId !== order.orderId)?.orderId}
                setOrderExtra={handleAddInternalRoomate}
              />
            ) : (
              <CustomInput
                moduleType="FIELD"
                prefixClassName="hotel cms-field person"
                type="text"
                value={referencePerson}
                onChange={e => {
                  setOrderExtra('HotelReferencePerson', e.target ? e.target.value : e);
                }}
                label={
                  <span>
                    <EventLabel name="ReferencePerson" groupName="CMS.Hotel" /> <span className="text-danger ml-1">*</span>
                  </span>
                }
              />
            )}
          </div>
        )}
      </div>
    );
  }

  if (hotelOrderRows.filter(or => or.orderId !== order.orderId).length > 0 && selectRoomMateFromOrder && order?.badgeNo && disableChangeLinkBooking) {
    return null;
  }

  const uniqueRooms = [...new Set(hotelOrderRows?.filter(({ orderRowId }) => orderRowId < 0)?.map(({ hotelGroupId }) => hotelGroupId))] ?? [];

  return (
    <div className="cms-hotel-wrap w-full ">
      <Transition appear show={showNoResultMessage} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={hideNoResultMessage}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title className="mb-2">
                    <EventLabel name="NoResult" groupName="CMS.Hotel" />
                  </Dialog.Title>
                  <Dialog.Description>
                    <EventLabel name="NoResultDescription" groupName="CMS.Hotel" />
                  </Dialog.Description>
                  <button onClick={hideNoResultMessage} className="cms-button text-xs h-fit px-2 py-1 mt-2" style={{ lineHeight: 1.5, fontSize: 16 }}>
                    <EventLabel name="Ok" groupName="CMS.Hotel" />
                  </button>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      <Snackbar message={snackbarMessage} showSnackbar={showSnackbar && snackbarMessage} setShowSnackbar={setShowSnackbar} />
      {!required && allowedBooking && (
        <div className="content-wrapper cms-question questionnaire-group mb-4" style={{ backgroundColor: 'inherit' }}>
          <label>
            <EventLabel name="WantToStayAtHotel" groupName="CMS.Hotel" />
          </label>
          <RadioGroup
            className="cms-question checkbox-group"
            aria-labelledby="stay-at-hotel-radio-group"
            onChange={(e, value) => {
              handleStay(value === 'true' ? true : false);
            }}
          >
            <FormControlLabel
              className="option font-normal"
              classes={classes}
              control={<Radio checked={stay === true} checkedIcon={<RadioButtonCheckedIcon className="cms-checkbox" />} />}
              value={true}
              label={<EventLabel name="Yes" groupName="CMS.Hotel" />}
            />
            <FormControlLabel
              className="option font-normal"
              classes={classes}
              control={<Radio checked={stay === false} checkedIcon={<RadioButtonCheckedIcon className="cms-checkbox" />} />}
              value={false}
              label={<EventLabel name="No" groupName="CMS.Hotel" />}
            />
          </RadioGroup>
        </div>
      )}
      {stay === true && !fullbooked && allowedBooking && !disableRoomateBooking ? (
        <div className="content-wrapper cms-question questionnaire-group" style={{ backgroundColor: 'inherit' }}>
          <label>
            <EventLabel name="WantToBookARoom" groupName="CMS.Hotel" />
            <span className="text-danger ml-1">*</span>
          </label>
          <RadioGroup
            className="cms-question checkbox-group"
            aria-labelledby="book-hotel-radio-group"
            onChange={(e, value) => setBook(value === 'true' ? true : false)}
          >
            <FormControlLabel
              className="option font-normal"
              classes={classes}
              control={<Radio checked={book === true} checkedIcon={<RadioButtonCheckedIcon className="cms-checkbox" />} />}
              value={true}
              label={<EventLabel name="Yes" groupName="CMS.Hotel" />}
            />
            <FormControlLabel
              className="option font-normal"
              classes={classes}
              control={<Radio checked={book === false} checkedIcon={<RadioButtonCheckedIcon className="cms-checkbox" />} />}
              value={false}
              label={<EventLabel name="LivingWithARoomMate" groupName="CMS.Hotel" />}
            />
          </RadioGroup>
        </div>
      ) : null}
      <br />
      {stay && book === false && allowedBooking && (
        <div className="content-wrapper cms-field">
          {selectRoomMateFromOrder ? (
            <OrderAutoComplete eventId={eventId} referencePerson={referencePerson} orderId={order?.orderId} setOrderExtra={setOrderExtra} />
          ) : (
            <CustomInput
              moduleType="FIELD"
              prefixClassName="hotel cms-field person"
              type="text"
              value={referencePerson}
              onChange={e => setOrderExtra('HotelReferencePerson', e.target ? e.target.value : e)}
              label={
                <span>
                  <EventLabel name="ReferencePerson" groupName="CMS.Hotel" /> <span className="text-danger ml-1">*</span>
                </span>
              }
            />
          )}
        </div>
      )}
      {stay && book && allowedBooking && (
        <Search
          checkinDate={checkinDate}
          checkoutDate={checkoutDate}
          bookableRoomSizes={bookableRoomSizes}
          bookableDates={bookableDates}
          roomCount={roomCount}
          selectedSizes={selectedRoomSizes}
          gapDates={gapDates}
          oneSizePerSearch={oneSizePerSearch}
          search={search}
          fetching={fetching}
          hideRoomCountSelector={hideRoomCountSelector}
          hideEndDate={hideEndDate}
          hideStartDate={hideStartDate}
          lockStartDate={lockStartDate}
          lockEndDate={lockEndDate}
          onCheckinDateChange={setCheckinDate}
          onCheckoutDateChange={setCheckoutDate}
          onRoomsCountChange={setRoomCount}
          onRoomSizeChange={setRoomSize}
        />
      )}

      {fetched && book && allowedBooking && (maxBookCount > 0 ? maxBookCount > uniqueRooms.length : true) && (
        <SearchResults
          results={searchResults}
          currency={currency}
          maxBookCount={maxBookCount}
          bookedCount={uniqueRooms?.length}
          loading={fetching}
          fullbooked={fullbokedAllotments}
          displayEventSettings={displayEventSettings}
          onAdd={handelBookingAdd}
        />
      )}
      {/* If the registered user has more then one bookings Or if the hideBookingDetails is set to true only show the bookings if they are not yet created */}
      {bookings?.length > 0 && (hideBookingDetails ? hotelOrderRows?.filter(hor => hor.orderRowId < 0)?.length > 0 : true) && (
        <>
          <hr />
          <Bookings
            bookings={bookings}
            onBookingChange={handleBookingChange}
            onBookingDelete={hotelGroupId => {
              handelBookingDelete(hotelGroupId);
              validateBooking();
            }}
            hideRegisterLater={hideRegisterLater}
            onRoomChange={handleRoomChange}
            displayEventSettings={displayEventSettings}
            eventId={eventId}
            currency={currency}
            orders={orders}
            roomMates={roomMates}
            selectRoomMateFromOrder={selectRoomMateFromOrder}
          />
        </>
      )}
    </div>
  );
}

export default observer(Hotel);
